/* eslint-disable react/no-unused-state */
import isEmpty from 'lodash/isEmpty'
import getProp from 'lodash/get'
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Geocode from 'react-geocode'
import { withGmapsContext } from './WithGoogleApi'
import GmapsAddressInput from './GmapsAddressInput'
import GmapsWindow from './GmapsWindow'
import GmapsAutocomplete from './GmapsAutocomplete'
import { validLocation, getMapViewportFromAreas } from './utils'

Geocode.enableDebug()

const TheHeartOfKendall = { lat: 21.125681, lng: 82.79499799999996 }
class GmapsAddress extends Component {
  constructor(props) {
    super(props)
    Geocode.setApiKey(props.apiKey)

    this.state = {
      address: undefined,
      city: '',
      state: '',
      mapPosition: TheHeartOfKendall,
      markerPosition: TheHeartOfKendall,
      mapViewport: null,
      mapFirstShowing: true,
      currentLocation: null,
    }
  }

  async componentDidMount() {
    const newState = await this.getStateFromProps(this.props)
    this.setState(newState)
  }

  async componentDidUpdate(prevProps, prevState) {
    // chasing for data loading
    if (!prevState.dataJustLoaded) {
      const newState = await this.getStateFromProps(this.props)
      if (
        (this.props.areaMode === false && this.dataJustLoaded(prevState, newState, 'address')) ||
        (this.props.areaMode && this.dataJustLoaded(prevState, newState, 'currentAreaSelection'))
      ) {
        let mapUpdates = {}
        if (
          this.props.areaMode &&
          (isEmpty(newState.currentAreaSelection) && isEmpty(this.props.boundaries)) === false
        ) {
          const mapViewport = getMapViewportFromAreas([
            ...newState.currentAreaSelection,
            ...(this.props.boundaries || []),
          ])
          const mapViewportCenter = {
            lat: (mapViewport.ne.lat + mapViewport.sw.lat) / 2,
            lng: (mapViewport.ne.lng + mapViewport.sw.lng) / 2,
          }
          mapUpdates = { mapPosition: mapViewportCenter, markerPosition: mapViewportCenter, mapViewport }
        }
        if (this.props.areaMode === false) {
          mapUpdates = { mapPosition: newState.markerPosition }
        }

        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ ...prevState, ...newState, ...mapUpdates, dataJustLoaded: true })
      }
    }
  }
  dataJustLoaded = (prevData, newData, key) => prevData[key] === undefined && newData[key] !== undefined

  getStateFromProps = async props => {
    // STREET ADDRESS MODE
    const address = getProp(props.value, 'caption', '')
    const markerPosition = (validLocation(getProp(props.value, 'heart')) && props.value.heart) || TheHeartOfKendall
    if (isEmpty(address) && !isEmpty(markerPosition)) {
      const geoResp = await Geocode.fromLatLng(markerPosition.lat, markerPosition.lng)
      return this.getStreetAddrPartsFromGeoResult(geoResp.results[0])
    }
    return { address, markerPosition }
  }

  getStreetAddrPartsFromGeoResult = geoResult => {
    const addressArray = geoResult.address_components
    return {
      area:
        (addressArray.find(x => x.types.some(t => ['sublocality_level_1', 'locality'].includes(t))) || {}).long_name ||
        '',
      city: (addressArray.find(x => x.types[0] === 'administrative_area_level_2') || {}).long_name || '',
      state: (addressArray.find(x => x.types[0] === 'administrative_area_level_1') || {}).long_name || '',
      address: geoResult.formatted_address,
    }
  }

  handleMarkerDragEnd = async event => {
    const newLat = event.latLng.lat()
    const newLng = event.latLng.lng()
    this.setState({
      markerPosition: { lat: newLat, lng: newLng },
    })

    const geoCodeResp = await Geocode.fromLatLng(newLat, newLng)

    if (this.props.areaMode === false) {
      this.saveInStateStreetAddressDetails(newLat, newLng, geoCodeResp.results[0])
      return
    }

    const userAreaOptions = this.getUserAreaOptionsFromGeocodeResponse(null, geoCodeResp)
    this.setState({
      userAreaOptions,
      address: userAreaOptions[0].caption,
    })
  }

  saveInStateStreetAddressDetails = (newLat, newLng, geoResult, updateMapPos) => {
    const location = { lat: newLat, lng: newLng }
    this.setState({
      ...this.getStreetAddrPartsFromGeoResult(geoResult),
      markerPosition: location,
      ...(updateMapPos ? { mapPosition: location } : {}),
    })
  }

  onPlaceSelected = async place => {
    if (isEmpty(place.address_components)) {
      return
    }
    const placeAddress = place.formatted_address
    const latValue = place.geometry.location.lat()
    const lngValue = place.geometry.location.lng()

    if (!this.props.areaMode) {
      this.saveInStateStreetAddressDetails(latValue, lngValue, place, true)
      return
    }

    const geoCodeResp = await Geocode.fromLatLng(latValue, lngValue)
    const userAreaOptions = this.getUserAreaOptionsFromGeocodeResponse(placeAddress, geoCodeResp)
    this.setState({
      userAreaOptions,
      address: userAreaOptions[0].caption,
      markerPosition: userAreaOptions[0].heart,
    })
  }

  getUserAreaOptionsFromGeocodeResponse = (placeAddress, geoResp) => {
    const levels = ['locality', 'administrative_area_level_2', 'administrative_area_level_1']
    let minLevel = 0
    if (placeAddress) {
      const exactResult = geoResp.results.find(r => r.formatted_address === placeAddress)
      if (exactResult) {
        minLevel = Math.max(levels.findIndex(l => exactResult.types.some(t => t === l)), 0)
        // if (minLevel < 0) minLevel = 0
      }
    }

    const userAreaOptions = []
    // eslint-disable-next-line no-plusplus
    for (let id = 0; id < 3 - minLevel; id++) {
      const levelType = levels[minLevel + id]
      const r = geoResp.results.find(z => z.types.some(t => t === levelType))
      if (!r) continue // eslint-disable-line no-continue
      const { lat: b, lng: a } = r.geometry.bounds.northeast
      const { lat: y, lng: x } = r.geometry.bounds.southwest
      userAreaOptions.push({
        level: minLevel + id,
        caption: r.formatted_address,
        heart: { lat: r.geometry.location.lat, lng: r.geometry.location.lng },
        polygon: [{ lat: b, lng: a }, { lat: b, lng: x }, { lat: y, lng: x }, { lat: y, lng: a }],
      })
    }
    return userAreaOptions
  }

  handleAddressChange = event => {
    this.setState({ address: event.target.value })
  }

  render() {
    const { classes, inputComponent, inputProps, boundaries, areaMode } = this.props
    const inputPlaceholder = areaMode ? 'Search for an area center' : 'Enter your address'
    const {
      address,
      addedUserAreas,
      markerPosition,
      mapPosition,
    } = this.state
    return (
      <div>
        <div style={{ display: 'flex' }}>
         <GmapsAutocomplete
              onPlaceSelected={this.onPlaceSelected}
              inputComponent={inputComponent}
              inputProps={{
                fullwidth: "true",
                placeholder: inputPlaceholder,
                value: address || '',
                onChange: this.handleAddressChange,
                ...inputProps,
              }}
            />
        </div>
            <GmapsWindow
              containerElement={<div style={{ height: this.props.height }} />}
              mapElement={<div style={{ height: '100%' }} />}
              mapPosition={mapPosition}
              markerPosition={markerPosition}
              onMarkerDragEnd={this.handleMarkerDragEnd}
              zoom={this.props.zoom}
            />
      </div>
    )
  }
}

GmapsAddress.propTypes = {
  apiKey: PropTypes.string,
  areaMode: PropTypes.bool,
  height: PropTypes.string,
  zoom: PropTypes.number,
  inputComponent: PropTypes.elementType,
  inputProps: PropTypes.object,
  value: PropTypes.any,
}

GmapsAddress.defaultProps = {
  areaMode: false,
  height: '600px',
  zoom: 15,
  inputComponent: GmapsAddressInput,
  inputProps: {},
}

export default withGmapsContext(GmapsAddress)
